"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Loader2, Search, UserPlus, Users, X } from "lucide-react";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { useLinkHouseholdLead } from "../hooks/households";

interface LeadPick {
  id: string;
  name: string;
  phone?: string;
  state?: string;
  householdId?: string | null;
}

interface AddLeadToHouseholdDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName?: string;
  /** This lead's household, if it has one. */
  householdId: string | null;
  /** Already in this household; left out of the results. */
  memberIds: string[];
}

/** Search the agent's leads (navbar typeahead) and put the one they pick in this lead's household. */
export default function AddLeadToHouseholdDialog({
  open,
  onOpenChange,
  leadId,
  leadName,
  householdId,
  memberIds,
}: AddLeadToHouseholdDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LeadPick[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<LeadPick | null>(null);
  const linkMutation = useLinkHouseholdLead();

  const reset = () => {
    setQuery("");
    setResults([]);
    setSelected(null);
  };

  useEffect(() => {
    if (!open || selected) return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await handleApiCall<any>(async () =>
          api.get<any>({
            url: getApiUrl(`${endpoint.leads.leadTypeahead}?q=${encodeURIComponent(term)}&limit=8`),
            auth: true,
            options: { signal: controller.signal },
          }),
        );
        // The global response wrapper leaves { data: { items } } after handleApiCall (as the navbar reads it).
        const items = resp?.data?.items ?? resp?.items;
        const arr: any[] = Array.isArray(items) ? items : [];
        setResults(
          arr.map((l: any) => ({
            id: l.id,
            name: l.name || l.email || "Lead",
            phone: l.phone || undefined,
            state: l.state || undefined,
            householdId: l.householdId ?? null,
          })),
        );
      } catch {
        if (!controller.signal.aborted) setResults([]);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, selected]);

  const candidates = results.filter((l) => l.id !== leadId && !memberIds.includes(l.id));

  const handleAdd = () => {
    if (!selected) return;
    linkMutation.mutate(
      { leadId, memberLeadId: selected.id },
      {
        onSuccess: () => {
          onOpenChange(false);
          reset();
        },
      },
    );
  };

  const details = (lead: LeadPick) =>
    [lead.phone ? formatPhoneNumber(lead.phone) : null, lead.state].filter(Boolean).join(" · ") ||
    "No phone on file";
  const inOtherHousehold = (lead: LeadPick) => !!lead.householdId && lead.householdId !== householdId;
  const thisLead = leadName || "This lead";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Add to household
          </DialogTitle>
          <DialogDescription>
            Search your leads and pick someone who lives with {thisLead}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          {selected ? (
            <>
              <div className="flex items-center justify-between rounded-lg border bg-gray-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{selected.name}</p>
                  <p className="text-xs text-gray-500">{details(selected)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setSelected(null)}
                  aria-label="Change lead"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {inOtherHousehold(selected) && (
                <p className="text-xs text-gray-600">
                  {householdId
                    ? `${selected.name} will leave their current household.`
                    : `${thisLead} will join ${selected.name}'s household.`}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  autoFocus
                  className="pl-8"
                  placeholder="Search by name or phone…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {searching && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              {query.trim().length >= 2 && (
                <div className="rounded-lg border divide-y max-h-56 overflow-y-auto">
                  {candidates.length === 0 && !searching ? (
                    <p className="px-3 py-2.5 text-xs text-gray-500">No matching leads.</p>
                  ) : (
                    candidates.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                        onClick={() => setSelected(lead)}
                      >
                        <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
                        <p className="text-xs text-gray-500">
                          {details(lead)}
                          {inOtherHousehold(lead) ? " · In another household" : ""}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleAdd} disabled={!selected || linkMutation.isPending}>
            {linkMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            Add to household
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
